import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";
import {
  openedDocItemStateAtom,
  openedDocumentAtom,
} from "atoms/documentsAtom";
import useDocuments from "features/Documents/useDocuments";

import { Input } from "components/Input";
import { Select } from "components/Select";
import { ItemSelect } from "components/ItemSelect";
import { Divider, VerticalSpace } from "components/Spacing";
import { RegularText } from "components/Text";
import { Spinner } from "components/Feedback";

import styles from "./styles.module.scss";
import { toQtyNumber } from "features/Documents/docQtyUtils";

/**
 * Единая форма позиции документа. Состав полей зависит от типа документа:
 * 1 Продажи:       товар, ед.изм, артикул | кол-во, цена, итого | комментарий
 * 2,7,9:           товар, ед.изм | кол-во, цена, итого
 * 3 Списания:      товар, ед.изм | кол-во
 * 4 Уценки:        из товара + ед.изм | в товар + ед.изм | кол-во
 * 5 Поступления:   товар, ед.изм, прайслист | кол-во, цена, % наценки, прод. цена, итого
 * 6 Перемещения:   товар, ед.изм, артикул | кол-во
 * 8 Инвентаризация: товар, ед.изм, артикул | кол-во учёт (ро), кол-во факт, расхождение (ро)
 */

const round2 = (val) => (Number.isFinite(val) ? val.toFixed(2) : "");

/** Общая ширина лейбла — выравнивает левый край инпутов, «Комментарий» не впритык */
const LABEL = 110;
const ROW_GAP = 56;
const MEASURE_W = 180;
const ARTIKUL_W = 180;
const PRICELIST_W = 200;
const INV_LABEL = 220;
const VALUE_W = 140;

const FieldRow = ({ children }) => (
  <div className={styles.fieldRow}>{children}</div>
);

export const DocItemForm = ({ docType, referenceData }) => {
  const { measureUnitsOptions, priceListsOptions } = referenceData;
  const { getItemLeftAmount } = useDocuments();

  const [openedDocument] = useAtom(openedDocumentAtom);
  const [state, setState] = useAtom(openedDocItemStateAtom);
  const [leftAmountLoading, setLeftAmountLoading] = useState(false);

  const type = Number(docType);
  const patch = (update) => setState((prev) => ({ ...prev, ...update }));

  const inventoryDifference = useMemo(
    () =>
      toQtyNumber(state.amount) - toQtyNumber(state.realamount),
    [state.amount, state.realamount]
  );

  const hasPrice = [1, 2, 5, 7, 9].includes(type);
  const hasArtikul = [1, 6, 8].includes(type);
  const hasComment = type === 1;
  const hasPricelist = type === 5 || type === 9;
  const isInventory = type === 8;
  const isRepricing = type === 4;

  const [selectedItemData, setSelectedItemData] = useState(null);

  const measureVal = useMemo(
    () =>
      measureUnitsOptions.find(
        (o) => String(o.value) === String(state.measureid ?? "")
      ) || null,
    [measureUnitsOptions, state.measureid]
  );

  const measure2Val = useMemo(
    () =>
      measureUnitsOptions.find(
        (o) => String(o.value) === String(state.measure2id ?? "")
      ) || null,
    [measureUnitsOptions, state.measure2id]
  );

  const pricelistVal = useMemo(
    () =>
      priceListsOptions.find(
        (o) => String(o.value) === String(state.pricelistid ?? "")
      ) || null,
    [priceListsOptions, state.pricelistid]
  );

  const itemArtikul = selectedItemData?.code ?? "";

  // Ширина селекта товара: левый край как у «Ед. изм», правый — как у Артикула/Прайслиста
  const itemSelectWidth = useMemo(() => {
    if (hasArtikul) {
      return MEASURE_W + ROW_GAP + LABEL + ARTIKUL_W + 48;
    }
    if (hasPricelist) {
      return MEASURE_W + ROW_GAP + LABEL + PRICELIST_W + 48;
    }
    return MEASURE_W + ROW_GAP + 260;
  }, [hasArtikul, hasPricelist]);

  /* ================= вычисления ================= */

  const handleAmount = (value) => {
    const update = { amount: value };
    if (hasPrice) {
      update.total = round2(Number(value) * Number(state.price));
    }
    patch(update);
  };

  const handlePrice = (value) => {
    const update = { price: value };
    update.total = round2(Number(value) * Number(state.amount));

    if (type === 5) {
      update.salepercent = state.frontprice
        ? String(
            (
              ((Number(state.frontprice) - Number(value)) / Number(value)) *
              100
            ).toFixed(0)
          )
        : "";
    }
    patch(update);
  };

  const handleTotal = (value) => {
    const update = { total: value };
    const price = round2(Number(value) / Number(state.amount));
    update.price = price;

    if (type === 5) {
      update.salepercent = state.frontprice
        ? String(
            (
              ((Number(state.frontprice) - Number(price)) / Number(price)) *
              100
            ).toFixed(0)
          )
        : "";
    }
    patch(update);
  };

  const handleSalePercent = (value) => {
    patch({
      salepercent: value,
      frontprice: round2(
        Number(state.price) + Number(state.price) * (Number(value) / 100)
      ),
    });
  };

  const handleFrontPrice = (value) => {
    patch({
      frontprice: value,
      salepercent: state.price
        ? String(
            (
              ((Number(value) - Number(state.price)) / Number(state.price)) *
              100
            ).toFixed(0)
          )
        : "",
    });
  };

  /* ================= выбор товара ================= */

  const getItemPriceForPricelist = (item, pricelistid) => {
    if (!item?.prices?.length || pricelistid == null) return null;
    const price = item.prices.find(
      (p) => String(p.pricelistid) === String(pricelistid)
    );
    return price ? price.price : null;
  };

  const getDefaultPrice = (item) =>
    getItemPriceForPricelist(item, openedDocument?.store?.pricelistid) ?? 0;

  const handlePricelist = (val) => {
    const pricelistid = val?.value ?? null;
    patch({ pricelistid });
    if (type !== 5) return;

    const item = selectedItemData || state.itemData;
    const price = getItemPriceForPricelist(item, pricelistid);
    if (price != null) handleFrontPrice(String(price));
  };

  const handleItem = async (option, item) => {
    setSelectedItemData(item || null);

    const update = {
      itemid: option?.value ?? null,
      itemData: item || null,
      measureid: item?.measureid || null,
    };

    if (type === 5) {
      update.pricelistid = openedDocument?.store?.pricelistid || null;
      patch(update);
      const defaultPrice = getDefaultPrice(item);
      if (defaultPrice) handleFrontPrice(String(defaultPrice));
      return;
    }

    if (type === 9) {
      update.pricelistid = openedDocument?.store?.pricelistid || null;
      patch(update);
      return;
    }

    // Автоподстановка цены из прайса склада — только для продаж и возвратов
    if (type === 1 || type === 2) {
      patch(update);
      handlePrice(String(getDefaultPrice(item)));
      return;
    }

    patch(update);

    if (isInventory) {
      if (option?.value) {
        setLeftAmountLoading(true);
        try {
          const leftAmount = await getItemLeftAmount(option.value);
          patch({ realamount: String(toQtyNumber(leftAmount)) });
        } finally {
          setLeftAmountLoading(false);
        }
      } else {
        patch({ realamount: "0" });
      }
    }
  };

  const handleItem2 = (option, item) => {
    patch({
      item2id: option?.value ?? null,
      item2Data: item || null,
      measure2id: item?.measureid || null,
    });
  };

  /* ================= рендер ================= */

  return (
    <div className={styles.form}>
      <FieldRow>
        <ItemSelect
          label={isRepricing ? "Из товара" : "Товар"}
          labelWidth={LABEL}
          labelVariant="regular"
          value={state.itemid}
          onChange={handleItem}
          width={itemSelectWidth}
        />
      </FieldRow>

      <FieldRow>
        <Select
          label="Ед. изм"
          labelWidth={LABEL}
          labelVariant="regular"
          options={measureUnitsOptions}
          value={measureVal}
          onChange={(val) => patch({ measureid: val?.value ?? null })}
          placeholder="Выберите ед. изм"
          width={MEASURE_W}
        />
        {hasArtikul && (
          <Input
            label="Артикул"
            labelVariant="regular"
            labelWidth={LABEL}
            value={itemArtikul}
            disabled
            inputWidth={ARTIKUL_W}
          />
        )}
        {hasPricelist && (
          <Select
            label="Прайслист"
            labelWidth={LABEL}
            labelVariant="regular"
            options={priceListsOptions}
            value={pricelistVal}
            onChange={handlePricelist}
            placeholder="Выберите прайслист"
            width={PRICELIST_W}
          />
        )}
      </FieldRow>

      {isRepricing && (
        <>
          <VerticalSpace size={16} />
          <Divider />
          <VerticalSpace size={16} />
          <FieldRow>
            <ItemSelect
              label="В товар"
              labelWidth={LABEL}
              labelVariant="regular"
              value={state.item2id}
              onChange={handleItem2}
              width={itemSelectWidth}
            />
          </FieldRow>
          <FieldRow>
            <Select
              label="Ед. изм"
              labelWidth={LABEL}
              labelVariant="regular"
              options={measureUnitsOptions}
              value={measure2Val}
              onChange={(val) => patch({ measure2id: val?.value ?? null })}
              placeholder="Выберите ед. изм"
              width={MEASURE_W}
            />
          </FieldRow>
        </>
      )}

      <VerticalSpace size={16} />
      <Divider />
      <VerticalSpace size={16} />

      {isInventory ? (
        <>
          <FieldRow>
            <Input
              label="Кол-во учётное"
              labelVariant="regular"
              labelWidth={INV_LABEL}
              type="number"
              value={state.realamount ?? "0"}
              disabled
              inputWidth={VALUE_W}
            />
            {leftAmountLoading && <Spinner size={16} />}
          </FieldRow>
          <FieldRow>
            <Input
              label="Кол-во фактическое + букеты"
              labelVariant="regular"
              labelWidth={INV_LABEL}
              type="number"
              value={state.amount ?? "0"}
              onChange={(ev) => patch({ amount: ev.target.value })}
              inputWidth={VALUE_W}
            />
          </FieldRow>
          <FieldRow>
            <Input
              label="Расхождение"
              labelVariant="regular"
              labelWidth={INV_LABEL}
              type="number"
              value={inventoryDifference}
              disabled
              inputWidth={VALUE_W}
            />
          </FieldRow>
          <VerticalSpace size={16} />
          <Divider />
          <VerticalSpace size={12} />
          <RegularText size={13} color="muted" className={styles.infoText}>
            Если вы поменяли дату документа, но не сохранили изменения, то
            учётное количество будет высчитываться относительно последней
            сохранённой даты
          </RegularText>
        </>
      ) : (
        <>
          <FieldRow>
            <Input
              label="Кол-во"
              labelVariant="regular"
              labelWidth={LABEL}
              type="number"
              value={state.amount ?? ""}
              onChange={(ev) => handleAmount(ev.target.value)}
              inputWidth={VALUE_W}
            />
          </FieldRow>
          {hasPrice && (
            <>
              <FieldRow>
                <Input
                  label="Цена"
                  labelVariant="regular"
                  labelWidth={LABEL}
                  type="number"
                  value={state.price ?? ""}
                  onChange={(ev) => handlePrice(ev.target.value)}
                  inputWidth={VALUE_W}
                />
                {type === 5 && (
                  <>
                    <Input
                      label="% наценки"
                      labelVariant="regular"
                      labelWidth={90}
                      type="number"
                      value={state.salepercent ?? ""}
                      onChange={(ev) => handleSalePercent(ev.target.value)}
                      inputWidth={110}
                    />
                    <Input
                      label="Прод. цена"
                      labelVariant="regular"
                      labelWidth={110}
                      type="number"
                      value={state.frontprice ?? ""}
                      onChange={(ev) => handleFrontPrice(ev.target.value)}
                      inputWidth={VALUE_W}
                    />
                  </>
                )}
              </FieldRow>
              <FieldRow>
                <Input
                  label="Итого"
                  labelVariant="regular"
                  labelWidth={LABEL}
                  type="number"
                  value={state.total ?? ""}
                  onChange={(ev) => handleTotal(ev.target.value)}
                  inputWidth={VALUE_W}
                />
              </FieldRow>
            </>
          )}
        </>
      )}

      {hasComment && (
        <>
          <VerticalSpace size={16} />
          <Divider />
          <VerticalSpace size={16} />
          <FieldRow>
            <Input
              label="Комментарий"
              labelVariant="regular"
              labelWidth={LABEL}
              value={state.itemcomment ?? ""}
              onChange={(ev) => patch({ itemcomment: ev.target.value })}
              inputWidth={itemSelectWidth}
            />
          </FieldRow>
        </>
      )}
    </div>
  );
};
